/**
 * The Support Inbox list page's state machine (design section 6.2/6.3).
 *
 * A hook rather than state inside `index.tsx`, for the reason the
 * notifications page gives: root jest compiles no `.tsx`, so logic that
 * lives in a component cannot be executed by a test. `react-test-renderer`
 * drives this hook, so the reads, the offset reset and the 403 path are all
 * proved by running them.
 *
 * TWO READS. The summary feeds the tab counts and the "waiting > 24 h" badge.
 * It is read on mount, on refresh, and — round 2, B4 — again after every
 * successful list read that a TAB CHANGE triggered, so the numbers on the
 * tabs follow the operator around the inbox (one extra GET per tab click; a
 * status, assignee or search change re-reads nothing, because those cannot
 * change how many threads are open). The list is keyed on every filter and
 * on the sort, and every filter change puts the offset back to 0: a page 3
 * of "Support" is not a page 3 of "Spam".
 *
 * A 403 is `accessDenied`, and NOTHING navigates. The route is already inside
 * `<RequirePermission>`, so a 403 here means the server holds a stricter view
 * than the panel's cached permission list (a role edit since sign-in, most
 * likely). The page says so in place; a redirect would hide the reason.
 *
 * ONLY THE NEWEST READ COUNTS. Each list read takes a sequence number, and a
 * response is applied only when its number is still the newest: an older,
 * slower response that lands after a newer one is discarded, so the rows can
 * never show one filter's page under another filter's controls, and
 * `loading` stays true until the read that matters has answered.
 *
 * THE SEARCH BOX IS DEBOUNCED. `q` is the box's value and changes on every
 * keystroke; the read is keyed on `query`, which follows `q` after
 * `SEARCH_DEBOUNCE_MS` of quiet. The offset reset happens when `query`
 * commits, not on the keystroke — a reset on the keystroke would re-read the
 * OLD query at offset 0 before the new one is even sent.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { nextSort, type SortState } from '@/components/table/sort';
import {
  PAGE_SIZE,
  READ_RESTRICTED_PERMISSION,
  SEARCH_DEBOUNCE_MS,
  errorMessage,
  errorStatus,
  fetchSummary,
  fetchThreads,
  visibleGroupTabs,
  type GroupTab,
  type GroupTabView,
  type SupportInboxSummaryData,
  type SupportThreadRow,
  type ThreadStatus,
} from './data';

export interface SupportInboxController {
  group: GroupTab;
  /** Switching the tab puts the offset back to 0. */
  setGroup: (group: GroupTab) => void;
  status: ThreadStatus | '';
  setStatus: (status: ThreadStatus | '') => void;
  /** `true` = the "Assigned to me" filter is on (`assigned=me`). */
  assigned: boolean;
  setAssigned: (on: boolean) => void;
  /** The search box's value — what the operator typed, updated on every keystroke. */
  q: string;
  setQ: (q: string) => void;
  /** The search the LIST was read with: `q` after `SEARCH_DEBOUNCE_MS` of quiet. */
  query: string;
  rows: SupportThreadRow[];
  total: number;
  offset: number;
  setOffset: (offset: number) => void;
  pageSize: number;
  summary: SupportInboxSummaryData | null;
  /** True while the FIRST list read is in flight, and while a later one is. */
  loading: boolean;
  /** The list read answered 403. Nothing navigates; the page says so in place. */
  accessDenied: boolean;
  /** The list read failed for another reason — the server's words. */
  error: string | null;
  sort: SortState;
  /** A column header click: inactive → desc → asc → cleared (`nextSort`). */
  setSortField: (field: string) => void;
  refresh: () => void;
  /** The tabs this admin sees; `Restricted` only with `support_inbox.read_restricted`. */
  tabs: GroupTabView[];
  canReadRestricted: boolean;
}

export function useSupportInboxPage(): SupportInboxController {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const canReadRestricted = hasPermission(READ_RESTRICTED_PERMISSION);

  const [group, setGroupState] = useState<GroupTab>('all');
  const [status, setStatusState] = useState<ThreadStatus | ''>('');
  const [assigned, setAssignedState] = useState(false);
  const [q, setQState] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<SortState>({ field: null, order: 'desc' });

  const [rows, setRows] = useState<SupportThreadRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<SupportInboxSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // The sequence number of the newest list read. A read applies its result
  // only while it still holds this number.
  const listSeq = useRef(0);
  // Set by a TAB change and cleared by the first successful list read after
  // it: that read re-reads the summary too, so the tab counts follow the tabs
  // (round 2, B4). A status, assignee or search change never sets it.
  const summaryStale = useRef(false);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await fetchSummary(apiUrl));
    } catch (e) {
      // The counts are decoration on the tabs; a failed summary must not
      // blank the list. A 403 here shows up on the list read too, which is
      // the one that says so.
      if (errorStatus(e) === 403) setAccessDenied(true);
      setSummary(null);
    }
  }, [apiUrl]);

  const loadList = useCallback(async () => {
    const seq = ++listSeq.current;
    const isNewest = () => seq === listSeq.current;
    setLoading(true);
    try {
      const data = await fetchThreads(apiUrl, {
        group,
        status,
        assignedToMe: assigned,
        q: query,
        offset,
        limit: PAGE_SIZE,
        sortField: sort.field,
        sortOrder: sort.order,
      });
      if (!isNewest()) return;
      setRows(data.threads);
      setTotal(data.total);
      setAccessDenied(false);
      setError(null);
      // A tab change landed: the counts on the tabs are re-read once.
      if (summaryStale.current) {
        summaryStale.current = false;
        void loadSummary();
      }
    } catch (e) {
      if (!isNewest()) return;
      setRows([]);
      setTotal(0);
      if (errorStatus(e) === 403) {
        setAccessDenied(true);
        setError(null);
      } else {
        setError(errorMessage(e));
      }
    } finally {
      if (isNewest()) setLoading(false);
    }
  }, [apiUrl, group, status, assigned, query, offset, sort.field, sort.order, loadSummary]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary, tick]);

  useEffect(() => {
    void loadList();
  }, [loadList, tick]);

  // The debounce: `query` follows `q` after a quiet interval. A keystroke
  // within the interval restarts it (the cleanup clears the pending timer).
  // Typing and deleting back to the committed value commits nothing, so no
  // read and no offset reset happen for a search that did not change.
  useEffect(() => {
    if (q === query) return undefined;
    const timer = setTimeout(() => {
      setQuery(q);
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q, query]);

  // Every filter change starts again at the first page. The setters are the
  // ONLY way the page changes a filter, so the reset cannot be forgotten by a
  // later caller.
  const setGroup = useCallback((next: GroupTab) => {
    summaryStale.current = true;
    setGroupState(next);
    setOffset(0);
  }, []);
  const setStatus = useCallback((next: ThreadStatus | '') => {
    setStatusState(next);
    setOffset(0);
  }, []);
  const setAssigned = useCallback((on: boolean) => {
    setAssignedState(on);
    setOffset(0);
  }, []);
  // The search box is the one filter whose offset reset is DEFERRED: it
  // happens when the debounced `query` commits (the effect above).
  const setQ = useCallback((next: string) => {
    setQState(next);
  }, []);
  const setSortField = useCallback((field: string) => {
    setSort((current) => nextSort(current, field));
    setOffset(0);
  }, []);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return {
    group,
    setGroup,
    status,
    setStatus,
    assigned,
    setAssigned,
    q,
    setQ,
    query,
    rows,
    total,
    offset,
    setOffset,
    pageSize: PAGE_SIZE,
    summary,
    loading,
    accessDenied,
    error,
    sort,
    setSortField,
    refresh,
    tabs: visibleGroupTabs(canReadRestricted),
    canReadRestricted,
  };
}
